import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/data";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total, shipping, freeShipping, freeShippingRemaining, grandTotal, checkout, loading } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-cream w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-rosegold/15">
          <SheetTitle className="font-display text-2xl text-maroon text-left">Your Cart</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3">
            <ShoppingBag className="w-10 h-10 text-warmneutral" strokeWidth={1.2} />
            <p className="text-inkbrown/60">Your cart is feeling a little empty.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="flex gap-4">
                <img src={i.image} alt={i.title} className="w-20 h-24 object-cover rounded-2xl" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <p className="font-medium text-inkbrown truncate">{i.title}</p>
                    <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.product_id}`} className="text-warmneutral hover:text-maroon transition-colors">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <p className="text-xs text-inkbrown/50 mt-0.5">
                    {[i.size, i.colour].filter(Boolean).join(" · ")}
                    {i.preorder && <span className="ml-2 text-rosegold">Pre-order</span>}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-3 border border-secondary rounded-full px-3 py-1">
                      <button onClick={() => updateQty(i.key, i.quantity - 1)} className="text-inkbrown/60 hover:text-maroon"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="text-sm w-4 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.key, i.quantity + 1)} className="text-inkbrown/60 hover:text-maroon"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <span className="text-maroon font-medium">{formatPrice(i.price * i.quantity, i.currency)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-rosegold/15 px-6 py-5 space-y-4">
            <div
              className={`rounded-2xl px-4 py-3 text-xs text-center ${freeShipping ? "bg-maroon/10 text-maroon" : "bg-subtle text-inkbrown/70"}`}
              data-testid="free-shipping-banner"
            >
              {freeShipping
                ? "🎉 You've unlocked free standard shipping!"
                : `You're ${formatPrice(freeShippingRemaining, items[0]?.currency)} away from free shipping.`}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-inkbrown/75" data-testid="cart-subtotal-row">
                <span>Subtotal</span>
                <span>{formatPrice(total, items[0]?.currency)}</span>
              </div>
              <div className="flex justify-between text-sm text-inkbrown/75" data-testid="cart-shipping-row">
                <span>Standard shipping</span>
                {freeShipping ? (
                  <span className="text-maroon font-medium">FREE</span>
                ) : (
                  <span>{formatPrice(shipping, items[0]?.currency)}</span>
                )}
              </div>
              <div className="flex justify-between items-baseline pt-2 border-t border-rosegold/10" data-testid="cart-total-row">
                <span className="text-inkbrown">Total</span>
                <span className="font-display text-xl text-maroon">{formatPrice(grandTotal, items[0]?.currency)}</span>
              </div>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              data-testid="checkout-button"
              className="w-full h-13 py-4 rounded-full bg-maroon text-cream hover:bg-maroon-dark transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? "Preparing checkout…" : "Checkout with Shopify"}
            </button>
            <p className="text-xs text-center text-inkbrown/50">Secure checkout powered by Shopify.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
